<template>
  <div class="h-full overflow-auto">
    <!-- Top Navigation -->
    <!-- {{ dishData }} -->

    <div class="fixed top-0 left-0 right-0 z-20 pointer-events-none">
      <div class="mobile-status-padding"></div>
      <div
        class="py-4 px-6 flex justify-between items-center w-full max-w-lg mx-auto pointer-events-auto"
      >
        <button
          class="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-md rounded-full shadow-lg hover:scale-105 active:scale-95 transition-all text-slate-800"
          @click="$emit('close')"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path
              d="M15 19l-7-7 7-7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div class="flex gap-3">
          <button
            class="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-md rounded-full shadow-lg hover:scale-105 active:scale-95 transition-all"
            @click="toggleFavorite"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              :fill="isFavorite ? '#FF5A5F' : 'none'"
              :stroke="isFavorite ? '#FF5A5F' : 'currentColor'"
              stroke-width="2"
            >
              <path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.72-8.72 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
              />
            </svg>
          </button>
          <button
            class="w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-md rounded-full shadow-lg hover:scale-105 active:scale-95 transition-all text-slate-800"
            @click="shareDish"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Hero Image Section -->
    <div class="sticky top-0">
      <div class="mobile-status-padding"></div>
      <div class="w-full py-[35%]">
        <div class="w-full absolute top-0 left-0 h-full">
          <div class="w-full h-full relative overflow-hidden">
            <div
              class="absolute w-full h-full duration-300 object-cover shimmerElementRoundedDefault"
            ></div>
            <ImgComponent
              class="absolute w-full h-full duration-300 object-cover"
              src="/images/placeholders/restaurant-dishes.png"
              alt=""
              :class="{ 'opacity-0': !showPlaceHolder }"
            />
            <ImgComponent
              v-if="dishData.principalImageUrl"
              class="relative w-full h-full object-cover scale-[1.15]"
              :src="dishData.principalImageUrl"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Content Container -->
    <div class="bg-white rounded-t-3xl py-8 -mb-8 -mt-8 relative"></div>
    <div class="relative bg-white">
      <!-- Dish Info -->
      <div class="px-6">
        <div class="flex justify-between">
          <div class="text-3xl font-bold text-gray-800">
            {{ dishData.title }}
          </div>
          <div class="flex flex-col gap-1 items-end">
            <!-- Unavailable badge -->
            <div
              v-if="!dishData.isPublished && dishData.isPublished !== undefined"
              class="px-3 py-1 rounded-full text-xs font-medium bg-gray-500 text-white"
            >
              Indisponible
            </div>

            <!-- Promotion badge -->
            <div
              v-if="dishData.promotion && dishData.isPublished"
              class="px-3 py-1 rounded-full text-xs font-bold bg-primary text-white flex items-center gap-1 shadow-md shadow-primary/30"
            >
              <template v-if="dishData.promotion.discountType === 'percentage'">
                -{{ dishData.promotion.discount }}%
              </template>
              <template v-else-if="dishData.promotion.discountType === 'fixed'">
                -{{ dishData.promotion.discount }} F
              </template>
            </div>

            <!-- Top seller badge -->
            <div
              v-if="dishData.isTopSeller && dishData.isPublished"
              class="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white flex items-center gap-1"
            >
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
              Top
            </div>
            <!-- Favorite badge -->
            <div
              v-if="dishData.isFavorite && dishData.isPublished"
              class="px-3 py-1 rounded-full text-xs font-medium bg-pink-500 text-white flex items-center gap-1"
            >
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                  clip-rule="evenodd"
                />
              </svg>
              Favori
            </div>
            <!-- Restaurant closed badge -->
            <div
              v-if="!isRestaurantOpen && dishData.restaurant"
              class="px-3 py-1 rounded-full text-xs font-medium bg-red-500 text-white"
            >
              Fermé
            </div>
          </div>
        </div>

        <div class="py-3" v-if="dishData.description"></div>
        <div class="text-lg text-gray-600" v-if="dishData.description">
          {{ dishData.description }}
        </div>

        <div class="py-2"></div>

        <div class="flex gap-8">
          <div class="flex items-end gap-2">
            <div
              class="whitespace-nowrap flex gap-1 title-color font-bold"
              :class="
                dishData.promotion ? 'text-2xl underline' : 'text-2xl underline'
              "
            >
              {{ displayPrice }} FCFA
            </div>
            <div
              v-if="dishData.promotion"
              class="text-sm text-slate-400 line-through font-medium mb-[3px]"
            >
              {{ dishData.price }} FCFA
            </div>
          </div>
          <div class="flex gap-2 items-center">
            <div class="w-6 h-6">
              <ColorImg
                src="/images/all/dish.svg"
                alt=""
                class="w-full h-full bg-primary"
              />
            </div>
            <div class="font-medium text-gray-700">
              {{ dishData.cookDurationInMin }}min
            </div>
          </div>
        </div>
      </div>

      <div class="py-4"></div>

      <!-- Ingredients Section -->
      <!-- <div class="px-6">
        <div
          class="flex py-6 gap-4 px-6 items-center ripple rounded-2xl bg-[#F4F4F5]"
          @click="showIngredients"
        >
          <div class="w-12 h-12 p-2 rounded-2xl bg-white">
            <ImgComponent
              class="w-full h-full object-contain"
              src="/images/all/recipes.svg"
              alt=""
            />
          </div>
          <div>
            <span class="font-bold">Ingrédients du repas</span>
          </div>
          <div class="w-3 h-3 ml-auto">
            <ImgComponent src="/images/all/right-arrow.svg" alt="" />
          </div>
        </div>
      </div>

      <div class="py-4"></div> -->

      <!-- Restaurant closed warning -->
      <div v-if="!isRestaurantOpen && dishData.restaurant" class="px-6 py-2">
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <div
              class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0"
            >
              <svg
                class="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div class="flex-1">
              <div class="text-lg font-bold text-red-800">Restaurant fermé</div>

              <!-- Temporarily unavailable -->
              <template v-if="dishData.restaurant?.isVisible === false">
                <div class="text-red-600 mt-1">
                  Ce restaurant est temporairement indisponible.
                </div>
                <div class="text-red-700 mt-1 text-sm">
                  Veuillez réessayer plus tard. Le restaurant sera bientôt de
                  retour.
                </div>
              </template>

              <!-- Outside opening hours -->
              <template v-else>
                <div class="text-red-600 mt-1">
                  Hors des heures d'ouverture.
                </div>
                <div class="text-red-700 mt-1 text-sm">
                  Revenez pendant les heures d'ouverture du restaurant.
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Options Section -->
    <div class="relative z-2 bg-white" v-if="dishDataWithOptions">
      <DishOptionsChoices
        :dishData="dishDataWithOptions"
        :selectedOptions="selectedOptions"
        @update:options="handleOptionsUpdate"
      />
    </div>
    <!-- {{ dishData.storeId || dishData.restaurantId }} -->
    <!-- Drinks Section -->
    <!-- <div class="p-6 relative z-2 bg-white" v-if="dishDataWithOptions">
      <DrinkChoices
        :dishData="dishDataWithOptions"
        :restaurantId="dishData.storeId || dishData.restaurantId"
        :selectedDrinks="selectedDrinks"
        @update:drinks="handleDrinksUpdate"
      />
    </div> -->

    <!-- Bottom spacing for cart controls -->
    <div
      class="h-[60px] z-[2] relative"
      style="background: linear-gradient(to top, #fff 10%, #0000)"
    ></div>
    <div class="h-[90px] bg-white z-[2] relative"></div>

    <!-- Cart Controls -->
    <CartControls
      :dishData="dishData"
      :canOrder="canOrder"
      :isRestaurantOpen="isRestaurantOpen"
      :itemQuantity="itemQuantity"
      :totalPrice="totalPrice"
      :isAddedToCart="isAddedToCart"
      @add-to-cart="handleAddToCart"
      @remove-from-cart="handleRemoveFromCart"
      @increase="handleIncrease"
      @decrease="handleDecrease"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  getCurrentInstance
} from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import ImgComponent from '@ui/core/img/ImgComponent.global.vue';
import CartControls from './components/CartControls.vue';

const { proxy } = getCurrentInstance();

const props = defineProps({
  dishData: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['close']);

const showPlaceHolder = ref(false);
const selectedOptions = ref([]);
const selectedDrinks = ref([]);
const currentCart = ref(null);
const isInitializingFromCart = ref(true);
const normalCartLoaded = ref(false);
const tempCartLoaded = ref(false);
const tempCartData = ref(null);

// Track when both carts are loaded
const bothCartsLoaded = computed(() => {
  return normalCartLoaded.value && tempCartLoaded.value;
});

// Get userId
const getUserId = () => {
  const userStore = window.$stores?.user;
  if (userStore?.state?.current?.id) {
    return userStore.state.current.id;
  }
  const userId = localStorage.getItem('user_id');
  if (userId) {
    return userId;
  }
  const tempUserId = 'temp-user-' + Date.now();
  localStorage.setItem('user_id', tempUserId);
  return tempUserId;
};

// Computed properties
const isRestaurantOpen = computed(() => {
  return props.dishData?.isRestaurantOpen !== false;
});

const canOrder = computed(() => {
  return (
    props.dishData?.canOrder !== false &&
    isRestaurantOpen.value &&
    props.dishData?.isPublished !== false
  );
});

// Calculate display price (with promotion if available)
const displayPrice = computed(() => {
  if (props.dishData?.promotion?.promotionalPrice) {
    return Math.round(props.dishData.promotion.promotionalPrice);
  }
  return props.dishData?.price || 0;
});

const dishDataWithOptions = computed(() => {
  return props.dishData;
});

const isFavorite = computed(() => {
  const favoriteDishes =
    window.$stores?.user?.state?.current?.favoriteDishes || [];
  return favoriteDishes.includes(props.dishData?.id);
});

// Check if dish is in cart
const cartItem = computed(() => {
  const cartItems = currentCart.value?.items || [];
  const found = cartItems.find((item) => item.dishId === props.dishData?.id);
  console.log('[DishDetail] cartItem computed:', found);
  console.log('[DishDetail] Searching dishId:', props.dishData?.id);
  console.log(
    '[DishDetail] Available items:',
    cartItems.map((i) => ({ dishId: i.dishId, quantity: i.quantity }))
  );
  return found;
});

const isAddedToCart = computed(() => {
  return !!cartItem.value;
});

const itemQuantity = computed(() => {
  return cartItem.value
    ? cartItem.value?.quantity || 1
    : tempCartData.value?.item?.quantity || 1;
});

const totalPrice = computed(() => {
  return cartItem.value
    ? cartItem.value?.totalPrice || 1
    : tempCartData.value?.item?.totalPrice || 1;
});

// Update currentCart from store
const updateCartFromStore = () => {
  const newCart = window.$stores?.cart?.state?.cart || null;
  console.log('[DishDetail] updateCartFromStore called:', newCart);
  console.log('[DishDetail] Looking for dishId:', props.dishData?.id);
  console.log('[DishDetail] Cart items:', newCart?.items);
  currentCart.value = newCart;
};

const toggleFavorite = () => {
  if (!props.dishData?.id) return;
  bus.emit(constants.EVENTS.USER.FAVORITE_TOGGLE_DISH, {
    dishId: props.dishData.id,
    isFavorite: isFavorite.value
  });
};

const shareDish = () => {
  if (!props.dishData) return;
  bus.emit(
    constants.EVENTS?.SHARER?.DISH?.SHOW || 'sharer.dish.show',
    props.dishData
  );
};

// Methods
const showIngredients = () => {
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    type: 'info',
    title: 'Ingrédients',
    body: 'Liste des ingrédients bientôt disponible'
  });
};

const handleOptionsUpdate = (options) => {
  selectedOptions.value = options;

  // If item is already in cart, update options on server
  if (isAddedToCart.value && cartItem.value) {
    bus.emit(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATE, {
      itemId: cartItem.value.id,
      options: options
    });
  } else {
    // Save to tempCart
    saveToTempCart();
  }
};

const handleDrinksUpdate = (drinks) => {
  selectedDrinks.value = drinks;

  // If item is already in cart, update drinks on server
  if (isAddedToCart.value && cartItem.value) {
    bus.emit(constants.EVENTS.CART.ITEMS.DRINKS.UPDATE, {
      itemId: cartItem.value.id,
      drinks: drinks
    });
  } else {
    // Save to tempCart
    saveToTempCart();
  }
};

// Save current configuration to tempCart
const saveToTempCart = () => {
  if (!props.dishData?.id) return;

  const userId = getUserId();
  const dishId = props.dishData.id;

  bus.emit(constants.EVENTS.CART.TEMP_CART_UPDATED, {
    userId,
    dishId,
    itemData: {
      dishId: props.dishData.id,
      title: props.dishData.title,
      name: props.dishData.title,
      price: displayPrice.value,
      quantity: tempCartData.value?.item?.quantity || 1,
      storeId: props.dishData.storeId,
      storeName:
        props.dishData.restaurant?.name ||
        props.dishData.storeName ||
        'Restaurant',
      currency: props.dishData.currency,
      fullOptions: props.dishData.options,
      fullAccompaniments: props.dishData.accompaniments,
      description: props.dishData.description,
      category: props.dishData.category,
      categoryId: props.dishData.categoryId,
      principalImageUrl:
        props.dishData.principalImageUrl || props.dishData.image,
      options: selectedOptions.value || [],
      drinks: selectedDrinks.value || [],
      promotion: props.dishData.promotion || null,
      originalPrice: props.dishData.price || null
    }
  });
};

// Load tempCart and initialize options/drinks
const loadTempCart = () => {
  if (!props.dishData?.id) return;

  const userId = getUserId();
  const dishId = props.dishData.id;

  console.log('[DishDetail] Loading tempCart for dish:', dishId);

  bus.emit(constants.EVENTS.CART.TEMP_CART_LOADING, {
    userId,
    dishId
  });
};

// Handle tempCart loaded
const handleTempCartLoaded = (data) => {
  console.log('[DishDetail] TempCart loaded:', data);
  // Controller cart emits { success, item }, store as { item: ... } for consistency
  tempCartData.value = data.success ? { item: data.item } : null;
  if (tempCartLoaded.value == true) {
    initializeFromCarts();
    return;
  }
  tempCartLoaded.value = true;
  console.log(
    '[DishDetail] normalCartLoaded:',
    normalCartLoaded.value,
    'tempCartLoaded:',
    tempCartLoaded.value
  );
};

const handleAddToCart = () => {
  // Emit proper cart event using EVENTS.CART.ITEMS.ADD
  console.log('🚀 ~ handleAddToCart ~ props.dishData:', props.dishData);
  bus.emit(constants.EVENTS.CART.ITEMS.ADD, {
    restaurantId: 
      props.dishData.restaurant?.name?.id ||
      props.dishData.restaurant?.id ||
      props.dishData.restaurantId,
    restaurant: props.restaurant,
    restaurantName:
      props.dishData.restaurant?.name?.name ||
      props.dishData.restaurant?.name ||
      props.dishData.storeName ||
      'Restaurant',
    dishId: props.dishData.id,
    title: props.dishData.title,
    name: props.dishData.title,
    price: displayPrice.value,
    quantity: itemQuantity.value,
    currency: props.dishData.currency,
    fullOptions: props.dishData.options,
    fullAccompaniments: props.dishData.accompaniments,
    description: props.dishData.description,
    category: props.dishData.category,
    categoryId: props.dishData.categoryId,
    principalImageUrl: props.dishData.principalImageUrl || props.dishData.image,
    options: selectedOptions.value || [],
    drinks: selectedDrinks.value || [],
    promotion: props.dishData.promotion || null,
    originalPrice: props.dishData.price || null
  });
};

const handleRemoveFromCart = () => {
  const item = cartItem.value;
  if (item) {
    // Save to tempCart before removing from normal cart
    saveToTempCart();

    bus.emit(constants.EVENTS.CART.ITEMS.REMOVE, { itemId: item.id });
  }
};

const handleIncrease = () => {
  const item = cartItem.value;
  if (item) {
    bus.emit(constants.EVENTS.CART.ITEMS.UPDATE, {
      itemId: item.id,
      quantity: item.quantity + 1
    });
  } else {
    // Not in normal cart, update tempCart quantity
    updateTempCartQuantity(1);
  }
};

const handleDecrease = () => {
  const item = cartItem.value;
  if (item && item.quantity > 1) {
    bus.emit(constants.EVENTS.CART.ITEMS.UPDATE, {
      itemId: item.id,
      quantity: item.quantity - 1
    });
  } else if (!item) {
    // Not in normal cart, update tempCart quantity (if > 1)
    updateTempCartQuantity(-1);
  }
};

// Update quantity in tempCart
const updateTempCartQuantity = (delta) => {
  if (!props.dishData?.id) return;

  const userId = getUserId();
  const dishId = props.dishData.id;

  // Calculate new quantity (min 1, max 99)
  const currentQuantity = tempCartData.value?.item?.quantity || 1;
  const newQuantity = Math.max(1, Math.min(99, currentQuantity + delta));

  console.log(
    '[DishDetail] Updating tempCart quantity:',
    currentQuantity,
    '->',
    newQuantity
  );

  bus.emit(constants.EVENTS.CART.TEMP_CART_UPDATED, {
    userId,
    dishId,
    itemData: {
      dishId: props.dishData.id,
      title: props.dishData.title,
      name: props.dishData.title,
      price: props.dishData.price,
      quantity: newQuantity,
      storeId: props.dishData.storeId || props.dishData.restaurantId,
      storeName:
        props.dishData.restaurant?.name ||
        props.dishData.storeName ||
        'Restaurant',
      currency: props.dishData.currency,
      description: props.dishData.description,
      category: props.dishData.category,
      categoryId: props.dishData.categoryId,
      principalImageUrl:
        props.dishData.principalImageUrl || props.dishData.image,
      options: selectedOptions.value || [],
      drinks: selectedDrinks.value || []
    }
  });
};

// Initialize options and drinks from the appropriate source (cart or tempCart)
const initializeFromCarts = () => {
  console.log('[DishDetail] initializeFromCarts called');
  console.log('[DishDetail] cartItem.value:', cartItem.value);
  console.log('[DishDetail] tempCartData.value:', tempCartData.value);
  console.log(
    '[DishDetail] isInitializingFromCart.value:',
    isInitializingFromCart.value
  );

  // Prioritize normal cart
  if (cartItem.value) {
    selectedOptions.value = cartItem.value.options || [];
    selectedDrinks.value = cartItem.value.drinks || [];
    isInitializingFromCart.value = false;
    console.log('[DishDetail] Initialized from normal cart:', {
      options: selectedOptions.value,
      drinks: selectedDrinks.value
    });
  } else if (tempCartData.value?.item) {
    // Fall back to tempCart
    selectedOptions.value = tempCartData.value.item.options || [];
    selectedDrinks.value = tempCartData.value.item.drinks || [];
    isInitializingFromCart.value = false;
    console.log('[DishDetail] Initialized from tempCart:', {
      options: selectedOptions.value,
      drinks: selectedDrinks.value
    });
  } else {
    // No data, initialize empty
    isInitializingFromCart.value = false;
    console.log('[DishDetail] Initialized empty (no cart or tempCart data)');
  }
};

// Listen to cart changes
const handleCartChange = () => {
  const previousCartItem = cartItem.value;
  updateCartFromStore();

  // Mark normal cart as loaded
  if (!normalCartLoaded.value) {
    normalCartLoaded.value = true;
    console.log('[DishDetail] Normal cart loaded');
  }

  // If item was removed from cart, tempCart will handle reinitialization
  if (previousCartItem && !cartItem.value) {
    console.log('[DishDetail] Item removed from cart');
    // Reinitialize from tempCart if available
    if (tempCartData.value?.item) {
      selectedOptions.value = tempCartData.value.item.options || [];
      selectedDrinks.value = tempCartData.value.item.drinks || [];
      console.log('[DishDetail] Reinitialized from tempCart after removal');
    }
  } else if (!isInitializingFromCart.value && cartItem.value) {
    // Item was added to cart, update from it
    selectedOptions.value = cartItem.value.options || [];
    selectedDrinks.value = cartItem.value.drinks || [];
    console.log('[DishDetail] Updated from cart change');
  }
};

onMounted(() => {
  setTimeout(() => {
    showPlaceHolder.value = true;
  }, 5000);

  // Listen to tempCart event
  bus.on(constants.EVENTS.CART.TEMP_CART_LOADED, handleTempCartLoaded);

  // Listen to cart events
  bus.on(constants.EVENTS.CART.LOADED, handleCartChange);
  bus.on(constants.EVENTS.CART.ITEMS.ADDED, handleCartChange);
  bus.on(constants.EVENTS.CART.ITEMS.UPDATED, handleCartChange);
  bus.on(constants.EVENTS.CART.ITEMS.REMOVED, handleCartChange);
  bus.on(constants.EVENTS.CART.ITEMS.OPTIONS.UPDATED, handleCartChange);
  bus.on(constants.EVENTS.CART.ITEMS.DRINKS_UPDATED, handleCartChange);
  bus.on(constants.EVENTS.CART.CLEARED, handleCartChange);

  // Load both normal cart and tempCart in parallel
  bus.emit(constants.EVENTS.CART.LOAD);
  loadTempCart();

  // Initialize cart from store immediately
  updateCartFromStore();

  // If cart already has data, mark it as loaded
  if (currentCart.value) {
    normalCartLoaded.value = true;
    console.log('[DishDetail] Normal cart already loaded from store');
  }
});

// Watch for when both carts are loaded, then initialize
watch(bothCartsLoaded, (isLoaded) => {
  if (isLoaded && isInitializingFromCart.value) {
    console.log('[DishDetail] Both carts loaded, initializing...');
    initializeFromCarts();
  }
});

onUnmounted(() => {
  bus.off(constants.EVENTS.CART.LOADED, handleCartChange);
  bus.off(constants.EVENTS.CART.ITEMS.ADDED, handleCartChange);
  bus.off(constants.EVENTS.CART.ITEMS.UPDATED, handleCartChange);
  bus.off(constants.EVENTS.CART.ITEMS.REMOVED, handleCartChange);
  bus.off(constants.EVENTS.CART.CLEARED, handleCartChange);
  bus.off(constants.EVENTS.CART.ITEMS.OPTIONS_UPDATED, handleCartChange);
  bus.off(constants.EVENTS.CART.ITEMS.DRINKS_UPDATED, handleCartChange);
  bus.off(constants.EVENTS.CART.TEMP_CART_LOADED, handleTempCartLoaded);
});
</script>
